import { Icon } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { FC } from 'react';
import {
  MdOutlineDescription,
  MdOutlineInsights,
  MdOutlineReceiptLong,
} from 'react-icons/md';
import { MText } from '../Monetize';
import { MBox, MFlex } from '../Monetize/chakra';
import { MontyAvatar } from './MontyAvatar';

interface ChatWelcomeProps {
  onSuggestionClick: (suggestion: string) => void;
  /** Route-aware prompts -- see `constants/chatHints.ts`. */
  suggestions: string[];
  /**
   * Only surfaced once hints are off, so the control to bring them back is
   * findable without adding chrome for everyone else.
   */
  hintsEnabled: boolean;
  onEnableHints: () => void;
}

const CAPABILITIES = [
  {
    icon: MdOutlineDescription,
    label: 'Quotes',
    description:
      'Summarize quotes, explain pricing outcomes, and track approval status.',
  },
  {
    icon: MdOutlineReceiptLong,
    label: 'Billing',
    description:
      'Explain invoice charges, surface credits, and flag anomalies.',
  },
  {
    icon: MdOutlineInsights,
    label: 'Insights',
    description: 'Renewals, forecasts, customer trends, and more.',
  },
];

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

export const ChatWelcome: FC<ChatWelcomeProps> = ({
  onSuggestionClick,
  suggestions,
  hintsEnabled,
  onEnableHints,
}) => {
  return (
    <MFlex
      direction="column"
      align="center"
      px={6}
      py={8}
      gap={6}
      maxW="540px"
      mx="auto"
      width="100%"
    >
      <MFlex direction="column" align="center" gap={4} textAlign="center">
        <MontyAvatar
          boxSize={14}
          borderRadius="2xl"
          boxShadow="0 12px 32px rgba(140, 50, 255, 0.35)"
          animation={`${float} 4s ease-in-out infinite`}
        />
        <MBox>
          <MText
            fontSize="2xl"
            fontWeight={600}
            color="fg.strong"
            letterSpacing="tight"
            lineHeight="1.2"
          >
            Hi, I'm Monty.
          </MText>
          <MText fontSize="sm" color="fg.muted" mt={2} lineHeight="1.5">
            Ask me anything about your quotes, billing, and customer data — no
            report building needed.
          </MText>
        </MBox>
      </MFlex>

      <MFlex direction="column" gap={2} width="100%">
        {CAPABILITIES.map(({ icon, label, description }) => (
          <MFlex
            key={label}
            align="flex-start"
            gap={3}
            p={3}
            borderRadius="lg"
            bg="bg.surface"
            border="1px solid"
            borderColor="border.subtle"
            transition="all 0.15s ease"
            _hover={{
              borderColor: 'tMagenta.200',
              boxShadow: 'sm',
            }}
          >
            <MFlex
              align="center"
              justify="center"
              boxSize={8}
              borderRadius="md"
              bg="tMagenta.50"
              color="tMagenta.base"
              flexShrink={0}
            >
              <Icon as={icon} boxSize={4} />
            </MFlex>
            <MBox>
              <MText
                fontSize="sm"
                fontWeight={600}
                color="fg.strong"
                lineHeight="1.3"
              >
                {label}
              </MText>
              <MText fontSize="xs" color="fg.muted" mt="2px" lineHeight="1.45">
                {description}
              </MText>
            </MBox>
          </MFlex>
        ))}
      </MFlex>

      <MFlex direction="column" gap={2} width="100%">
        <MText
          fontSize="xs"
          color="fg.subtle"
          fontWeight={600}
          textTransform="uppercase"
          letterSpacing="wider"
        >
          Try asking
        </MText>
        <MFlex direction="column" gap={1.5}>
          {suggestions.map((suggestion) => (
            <MBox
              key={suggestion}
              asChild
              onClick={() => onSuggestionClick(suggestion)}
              textAlign="left"
              px={3}
              py={2.5}
              borderRadius="lg"
              bg="bg.surface"
              border="1px solid"
              borderColor="border.subtle"
              fontSize="sm"
              color="fg.primary"
              cursor="pointer"
              transition="all 0.15s ease"
              _hover={{
                borderColor: 'tIndigo.base',
                bg: 'tBlue.hover',
                color: 'tIndigo.base',
                transform: 'translateY(-1px)',
                boxShadow: 'sm',
              }}
              _focusVisible={{
                outline: 'none',
                boxShadow: 'focus',
                borderColor: 'tIndigo.base',
              }}
            >
              <button type="button">{suggestion}</button>
            </MBox>
          ))}
        </MFlex>
      </MFlex>

      {!hintsEnabled && (
        <MBox
          asChild
          fontSize="xs"
          color="fg.subtle"
          textDecoration="underline"
          cursor="pointer"
          onClick={onEnableHints}
          data-testid="chat-enable-hints-btn"
          _hover={{ color: 'fg.muted' }}
        >
          <button type="button">Show me Monty's hints again</button>
        </MBox>
      )}
    </MFlex>
  );
};
